'use client';

import { usePathname } from '@/i18n/navigation';
import { pathMatches } from '@/lib/util/path';
import Container, { ContainerProps } from '@/ui/Container';
import { pathOverrides } from '@/ui/layout/NavBar/navbar-config';
import Responsive from '@/ui/Responsive/Responsive';
import { motion, Variants } from 'motion/react';
import { useEffect, useState } from 'react';
import { useAnimation, useScrollState } from './animation';
import styles from './NavBar.module.scss';
import NavBarContentDesktop from './NavBarContentDesktop';
import NavBarContentMobile from './NavBarContentMobile';
import { NavBarStyle } from './types';

export type NavBarProps = {
  topStyle?: NavBarStyle;
  scrolledStyle?: NavBarStyle;
  slotProps?: {
    container?: ContainerProps;
  };
};

export default function NavBar(props: NavBarProps) {
  const pathname = usePathname();

  // Apply path overrides
  for (const override of pathOverrides) {
    if (pathMatches(pathname, override.path, Boolean(override.exact)))
      props = { ...props, ...override.overrides };
  }

  // Get props
  const {
    topStyle = 'transparent',
    scrolledStyle = 'opaque',
    slotProps: { container: containerProps } = {},
  } = props;

  // Mobile menu open state
  const [isOpen, setIsOpen] = useState(false);

  // Scroll state handling
  const scrollState = useScrollState();

  // Style, variant, and theme handling
  const { variant, navBarTheme, logoVariant, transparent } = useAnimation(
    scrollState,
    topStyle,
    scrolledStyle,
    isOpen
  );

  // Animation variants
  const variants: Variants = {
    transparent: {
      backgroundColor: transparent,
      color: 'var(--color-heading)',
      boxShadow: 'none',
    },
    opaque: {
      backgroundColor: 'var(--color-navbar)',
      color: 'var(--color-heading)',
      boxShadow: 'var(--card-box-shadow)',
    },
  };

  // Disable body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isOpen]);

  return (
    <motion.nav
      className={styles.nav}
      variants={variants}
      initial="opaque"
      animate={variant}
      transition={{ duration: 0.4 }}
      data-theme={navBarTheme}
    >
      <Container {...containerProps}>
        <Responsive
          xs={
            <NavBarContentMobile
              open={isOpen}
              onToggle={setIsOpen}
              pathname={pathname}
              logoVariant={logoVariant}
            />
          }
          md={<NavBarContentDesktop pathname={pathname} logoVariant={logoVariant} />}
        />
      </Container>
    </motion.nav>
  );
}
